type ScrollToTopButtonProps = {
  className?: string;
  hiddenClassName?: string;
  footerElement?: HTMLElement | null;
  id?: string;
  innerHTML?: string;
}

export class ScrollToTopButton {
  button: HTMLElement;
  _className: string;
  _hiddenClassName: string;
  _footerElement: HTMLElement | null;
  _animationFrameId: number;

  constructor({
    className = "scroll-to-top-button",
    hiddenClassName = "scroll-to-top-button--hidden",
    innerHTML = "",
    footerElement = null,
    id = "scroll-to-top-button",
  }: ScrollToTopButtonProps) {
    this._footerElement = footerElement;
    this._className = className;
    this._hiddenClassName = hiddenClassName;

    const button = document.createElement("button");
    button.classList.add(className);
    button.classList.add(hiddenClassName);
    button.setAttribute("aria-label", "Scroll to top");
    button.id = id;
    button.innerHTML = innerHTML;

    this.button = button;
  }

  activate = () => {
    document.body.append(this.button);
    this._addEventListeners();

    this._animationFrameId = requestAnimationFrame(this._reconcileDom);
  }

  deactive = () => {
    this.button.remove();
    this._removeEventListeners();

    cancelAnimationFrame(this._animationFrameId);
  }

  _hideButton = () => {
    if (this.button.classList.contains(this._hiddenClassName)) {
      this.button.style.display = "none";
    }
  }

  _addEventListeners = () => {
    this.button.addEventListener("click", this._scrollToTop);
    this.button.addEventListener("transitionend", this._hideButton);
  }

  _removeEventListeners = () => {
    this.button.removeEventListener("click", this._scrollToTop);
    this.button.removeEventListener("transitionend", this._hideButton);
  }

  _scrollToTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }

  _reconcileDom = () => {
    const { innerHeight, scrollY } = window;
    const pageHeight = document.body.offsetHeight

    /** Bottom pixel of current scroll */
    const currentScrollBottom = innerHeight + scrollY;

    const footerHeight = this._footerElement 
    ? this._footerElement.offsetHeight 
    : 0;

    const scrollStart = innerHeight / 3;
    const scrollEnd = pageHeight - footerHeight - 50;
    const isNearPageTop = scrollY < scrollStart;
    const isNearPageBottom = scrollEnd < currentScrollBottom; 
    const isShortPage = (scrollEnd - scrollStart) < (innerHeight * 3);

    if (isNearPageTop || isNearPageBottom || isShortPage) {
      setTimeout(() => {
        this.button.classList.add(this._hiddenClassName);
      }, 0);
    } else {
      this.button.style.removeProperty("display");
      // Without a timeout, the animation will fire before the browser
      // registers that the display property has been removed. This will
      // cause the animation to be skipped entirely.
      setTimeout(() => {
        this.button.classList.remove(this._hiddenClassName);
      }, 0);
    }
    requestAnimationFrame(this._reconcileDom);
  }
}
